import {
  BusinessDetails,
  ContactDetails,
  FooterLink,
  GoogleDetails,
  MetaTagsProps,
  SocialMediaProfilesTypes,
} from "@/types";
import type {
  GoogleContactFormProps,
  GoogleContactFormSchemaField,
  GoogleContactFormServiceOption,
} from "@modes0/ui-components/client";

export const BUSINESS_NAME = "Ozy Wide Group";
export const WEBSITE_URL = "https://ozywidegroup.com.au";
export const OG_PATH = `${WEBSITE_URL}/images/og/`;

export const defaultMetaTags: MetaTagsProps = {
  title: `Sydney Security Services | CCTV, Alarms & Guards | ${BUSINESS_NAME}`,
  description:
    "Ozy Wide Group provides security services across Sydney, including CCTV installation, alarm systems, intercoms, access control, automatic gates, licensed guards, mobile patrols, fire alarm support and security cabling.",
  keywords:
    "Ozy Wide Group, Sydney security services, CCTV installation Sydney, alarm systems Sydney, access control Sydney, security guards Sydney, mobile patrols Sydney, intercom systems Sydney",
  url: WEBSITE_URL,
  ogImage: `${OG_PATH}home.jpg`,
  type: "website",
};

export const socialMediaProfiles: SocialMediaProfilesTypes = {
  instaLink: "https://www.instagram.com/",
  instaHandle: "ozywidegroup",
  instaPostsLink: "https://www.instagram.com/p/",
  tikTokLink: "https://www.tiktok.com/",
  tikTokHandle: "",
  tikTokPostsLink: "https://www.tiktok.com/",
  facebookLink: "https://www.facebook.com/",
  facebookHandle: "",
  linkedInLink: "https://www.linkedin.com/company/",
  linkedInHandle: "",
  youTubeHandle: "",
  youTubeLink: "https://youtube.com/",
};

export const contactDetails: ContactDetails = {
  contactNumberOne: "0456 666 111",
  contactNumberTwo: "",
  fax: "",
  contactEmail: "info@ozywidegroup.com.au",
};

export const footerLinks: FooterLink[] = [];

export const googleDetails: GoogleDetails = {
  googleHandle: "",
  googleLink: "",
  googleMaps: "",
  googleReviewLink: "",
  googleServiceArea: "",
  areaServed: "Sydney",
  serviceAreaLatLong: {
    latitude: -33.918,
    longitude: 151.0346,
  },
};

export const businessDetails: BusinessDetails = {
  branding: {
    headerLogoUrl: "/images/logos/header-logo.svg",
    footerLogoUrl: "/images/logos/footer-logo.svg",
  },
  companyData: {
    abn: "",
    address: "",
    licenseNumber: "Licence M/L 000108909",
  },
  captchaKeys: {
    dev: "6LeDLn4UAAAAAPW2RFlXvgusfkU9e_n4Q7zFtpQj",
    prod: "6LdE7SMtAAAAAFEhDjprtuQcotnwQgOzE_vIT5sm",
  },
  address: {
    streetAddress: "",
    addressLocality: "",
    addressRegion: "NSW",
    postalCode: "",
    addressCountry: "AU",
  },
  serviceAreas: [
    "Sydney CBD",
    "Inner West",
    "Eastern Suburbs",
    "Lower North Shore",
    "Upper North Shore",
    "Northern Beaches",
    "Western Sydney",
    "Southern Sydney",
    "Sutherland Shire",
    "Greater Western Sydney",
  ],
  generalServiceArea: "Sydney",
};

const contactFormApiUrl =
  process.env.NEXT_PUBLIC_CONTACT_FORM_API_URL ||
  "https://growleads.com.au/api/contact-form/";

const contactFormLocalApiUrl =
  process.env.NEXT_PUBLIC_CONTACT_FORM_LOCAL_API_URL ||
  "http://localhost:8080/api/contact-form";

const recaptchaSiteKey =
  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ||
  businessDetails.captchaKeys?.prod ||
  "";

const recaptchaDevSiteKey =
  process.env.NEXT_PUBLIC_RECAPTCHA_DEV_SITE_KEY ||
  businessDetails.captchaKeys?.dev ||
  recaptchaSiteKey ||
  "";

export const contactFormServiceOptions: GoogleContactFormServiceOption[] = [
  {
    label: "CCTV installation or maintenance",
    value: "CCTV installation or maintenance",
  },
  { label: "Alarm systems", value: "Alarm systems" },
  { label: "Intercom systems", value: "Intercom systems" },
  { label: "Access control systems", value: "Access control systems" },
  { label: "Automatic security gates", value: "Automatic security gates" },
  { label: "Security guards", value: "Security guards" },
  { label: "Mobile patrols", value: "Mobile patrols" },
  { label: "Security consultation", value: "Security consultation" },
  { label: "Fire alarm systems", value: "Fire alarm systems" },
  {
    label: "Data infrastructure or security cabling",
    value: "Data infrastructure or security cabling",
  },
  { label: "General security enquiry", value: "General security enquiry" },
];

export const contactFormBaseFields: GoogleContactFormSchemaField[] = [
  {
    name: "service",
    label: "What security service do you need?",
    type: "select",
    required: true,
    requiredMessage: "Please choose the security service you need.",
    placeholder: "Select a service",
    options: contactFormServiceOptions,
    icon: "shield",
  },
  {
    name: "suburb",
    label: "Property suburb",
    type: "text",
    required: true,
    requiredMessage: "Please enter the suburb for the property.",
    placeholder: "Sydney suburb",
    autoComplete: "address-level2",
    icon: "map-pin",
  },
  {
    name: "firstName",
    label: "First name",
    type: "text",
    required: true,
    requiredMessage: "Please enter your first name.",
    placeholder: "First name",
    autoComplete: "given-name",
    icon: "user",
    width: "half",
  },
  {
    name: "lastName",
    label: "Last name",
    type: "text",
    required: true,
    requiredMessage: "Please enter your last name.",
    placeholder: "Last name",
    autoComplete: "family-name",
    icon: "user",
    width: "half",
  },
  {
    name: "phone",
    label: "Phone number",
    type: "tel",
    required: true,
    requiredMessage: "Please enter your phone number.",
    placeholder: "0456 666 111",
    autoComplete: "tel",
    icon: "phone",
    width: "half",
  },
  {
    name: "email",
    label: "Email address",
    type: "email",
    required: true,
    requiredMessage: "Please enter your email address.",
    placeholder: "name@example.com",
    autoComplete: "email",
    icon: "mail",
    width: "half",
  },
  {
    name: "message",
    label: "Security details",
    type: "textarea",
    required: true,
    requiredMessage: "Please tell us what security support you need.",
    placeholder:
      "Tell us about the property, the security issue or service needed, timing, access details and any existing systems.",
    rows: 5,
    icon: "message",
  },
];

export const contactFormExtraFields: GoogleContactFormSchemaField[] = [
  {
    name: "propertyType",
    label: "Property type",
    type: "dropdown",
    placeholder: "Select property type",
    icon: "shield",
    width: "half",
    options: [
      { label: "Home or townhouse", value: "Home or townhouse" },
      { label: "Retail or hospitality", value: "Retail or hospitality" },
      { label: "Office", value: "Office" },
      { label: "Warehouse or industrial", value: "Warehouse or industrial" },
      { label: "Strata or shared entry", value: "Strata or shared entry" },
      { label: "Construction site", value: "Construction site" },
      { label: "Other", value: "Other" },
    ],
  },
  {
    name: "timeframe",
    label: "Ideal timeframe",
    type: "dropdown",
    placeholder: "Select timeframe",
    icon: "clock",
    width: "half",
    options: [
      { label: "As soon as possible", value: "As soon as possible" },
      { label: "This week", value: "This week" },
      { label: "Next 2-4 weeks", value: "Next 2-4 weeks" },
      { label: "Planning ahead", value: "Planning ahead" },
      { label: "Not sure yet", value: "Not sure yet" },
    ],
  },
  {
    name: "existingSystem",
    label: "Existing security system",
    type: "checkbox",
    checkboxLabel:
      "There is already CCTV, alarm, intercom or access-control equipment on site",
    checkedValue: "Yes",
    uncheckedValue: "No",
    icon: "check",
    width: "half",
  },
  {
    name: "needsAfterHours",
    label: "After-hours security",
    type: "checkbox",
    checkboxLabel:
      "After-hours guarding, patrols or alarm response may be needed",
    checkedValue: "Yes",
    uncheckedValue: "No",
    icon: "check",
    width: "half",
  },
];

export const contactFormWidgetContent = {
  section: {
    id: "ozy-wide-group-enquiry",
    titleId: "ozy-wide-group-enquiry-title",
    eyebrow: "Request a security quote",
    title: "Tell us what needs protecting, monitoring or controlling.",
    description:
      "Send the basics about your Sydney property, site or business. Ozy Wide Group will use those details to help with the right next step for systems, guards, patrols or security advice.",
  },
  captcha: {
    siteKey: recaptchaSiteKey,
    devSiteKey: recaptchaDevSiteKey,
  },
  localApiUrl: contactFormLocalApiUrl,
  form: {
    apiUrl: contactFormApiUrl,
    formName: "Ozy Wide Group Website Enquiry",
    schema: {
      fields: contactFormBaseFields,
    },
    extraFields: contactFormExtraFields,
    title: "Request a security quote",
    subtitle:
      "Share the essentials and Ozy Wide Group will reply with next steps.",
    submitText: "Send Enquiry",
    loadingText: "Sending...",
    successTitle: "Enquiry sent",
    successMessage: "Thanks, your enquiry has been sent to Ozy Wide Group.",
    errorTitle: "Could not send your enquiry.",
    errorMessage: "Please check the highlighted fields and try again.",
    captchaRequiredMessage: "Please tick the reCAPTCHA box before sending.",
    footerText: "Your details go straight to Ozy Wide Group.",
    loadingFooterText: "Sending your enquiry now...",
    errorFooterText:
      "Your information is kept private and only used to respond to this enquiry.",
    trustItems: [
      { label: "Sydney security", icon: "shield" },
      { label: "Systems and guards", icon: "shield" },
      { label: "Fast follow-up", icon: "clock" },
    ],
    accentColor: "#28aae1",
    hiddenPayload: {
      sourceComponent: "google-contact-form",
      sourceSite: "ozywidegroup.com.au",
      businessName: BUSINESS_NAME,
    },
    style: { maxWidth: 680 },
  } satisfies GoogleContactFormProps,
};

type ServiceContactFormContentInput = {
  serviceTitle: string;
  categoryName: string;
  categorySlug: string;
  serviceSlug: string;
  pageUrl: string;
};

const serviceContactStageOptions: GoogleContactFormServiceOption[] = [
  { label: "Scoping the security need", value: "Scoping the security need" },
  { label: "Ready for a quote", value: "Ready for a quote" },
  {
    label: "Ready to book installation or coverage",
    value: "Ready to book installation or coverage",
  },
  {
    label: "Need help choosing the right service",
    value: "Need help choosing the right service",
  },
  { label: "Not sure yet", value: "Not sure yet" },
];

export const createServiceContactFormContent = ({
  categoryName,
  categorySlug,
  pageUrl,
  serviceSlug,
  serviceTitle,
}: ServiceContactFormContentInput) => {
  const serviceLabel = serviceTitle.toLowerCase();

  return {
    captcha: {
      siteKey: recaptchaSiteKey,
      devSiteKey: recaptchaDevSiteKey,
    },
    localApiUrl: contactFormLocalApiUrl,
    form: {
      apiUrl: contactFormApiUrl,
      formName: `Ozy Wide Group ${serviceTitle} Enquiry`,
      schema: {
        fields: [
          {
            name: "suburb",
            label: "Property suburb",
            type: "text",
            required: true,
            requiredMessage: "Please enter the suburb for the property.",
            placeholder: "Sydney suburb",
            autoComplete: "address-level2",
            icon: "map-pin",
          },
          ...contactFormBaseFields.filter((field) =>
            ["firstName", "lastName", "phone", "email"].includes(field.name),
          ),
          {
            name: "projectStage",
            label: `Where is the ${serviceLabel} enquiry up to?`,
            type: "dropdown",
            required: true,
            requiredMessage: "Please choose the stage that best fits.",
            placeholder: "Choose a stage",
            options: serviceContactStageOptions,
            icon: "clock",
          },
          {
            name: "message",
            label: `${serviceTitle} details`,
            type: "textarea",
            required: true,
            requiredMessage: "Please tell us a little about the security work.",
            placeholder: `Tell us what you need for ${serviceLabel}, the property type, access details, timing and any existing systems.`,
            rows: 4,
            icon: "message",
          },
        ],
      },
      title: `${serviceTitle} enquiry`,
      subtitle: `Send the essentials for your ${serviceLabel} enquiry and Ozy Wide Group will reply with the next practical step.`,
      submitText: "Send Enquiry",
      loadingText: "Sending...",
      successTitle: "Enquiry sent",
      successMessage: `Thanks, your ${serviceLabel} enquiry has been sent to Ozy Wide Group.`,
      errorTitle: "Could not send your enquiry.",
      errorMessage: "Please check the highlighted fields and try again.",
      captchaRequiredMessage: "Please tick the reCAPTCHA box before sending.",
      footerText: "Your details go straight to Ozy Wide Group.",
      loadingFooterText: "Sending your enquiry now...",
      errorFooterText:
        "Your information is kept private and only used to respond to this enquiry.",
      trustItems: [],
      accentColor: "#28aae1",
      hiddenPayload: {
        sourceComponent: "service-contact-form",
        sourceSite: "ozywidegroup.com.au",
        businessName: BUSINESS_NAME,
        service: serviceTitle,
        serviceSlug,
        serviceCategory: categoryName,
        categorySlug,
        pageUrl,
      },
    } satisfies GoogleContactFormProps,
  };
};
